import React, { useEffect, useRef, useState } from "react";
import { Bell, Check, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";

const PRIORITY_DOT = {
  urgent: "bg-red-400",
  action: "bg-gold",
  success: "bg-emerald-400",
  update: "bg-white/40",
};

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export const NotificationBell = ({ onOpen }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = () => {
    api.get("/admin/notifications").then(({ data }) => {
      setItems(data.items || []);
      setUnread(data.unread || 0);
    }).catch(() => {});
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 20000); // lightweight poll for in-app center
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markAll = async () => {
    try { const { data } = await api.post("/admin/notifications/read", { all: true }); setUnread(data.unread); setItems((p) => p.map((x) => ({ ...x, read: true }))); } catch (e) { console.error("Failed to mark all notifications read:", e); }
  };
  const openItem = async (n) => {
    try { await api.post("/admin/notifications/read", { ids: [n.id] }); } catch (e) { console.error("Failed to mark notification read:", e); }
    setUnread((u) => Math.max(0, u - (n.read ? 0 : 1)));
    setItems((p) => p.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    setOpen(false);
    if (onOpen) onOpen(n.url);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="notification-bell"
        aria-label="Notifications"
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[color:var(--border-subtle)] text-white/80 hover:bg-white/5 transition-colors"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span data-testid="notification-unread-count" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-gold text-black text-[10px] font-bold flex items-center justify-center">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-panel" className="absolute right-0 mt-2 w-[340px] max-h-[70vh] overflow-hidden rounded-xl border border-[color:var(--border-strong)] bg-[#101011] shadow-2xl z-50 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[color:var(--border-subtle)]">
            <span className="text-sm font-semibold text-white">Notifications</span>
            <button onClick={markAll} data-testid="notification-mark-all" className="inline-flex items-center gap-1 text-xs text-gold-soft hover:text-gold"><CheckCheck className="h-3.5 w-3.5" /> Mark all read</button>
          </div>
          <div className="overflow-y-auto">
            {items.length === 0 && <p className="px-4 py-8 text-center text-sm text-muted-c">No notifications yet.</p>}
            {items.map((n) => (
              <button
                key={n.id}
                onClick={() => openItem(n)}
                data-testid="notification-item"
                className={`w-full text-left px-4 py-3 border-b border-[color:var(--border-subtle)]/50 hover:bg-white/[0.03] transition-colors ${n.read ? "opacity-70" : ""}`}
              >
                <div className="flex items-start gap-2.5">
                  <span className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${PRIORITY_DOT[n.priority] || "bg-white/40"}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-white truncate">{n.title}</p>
                    <p className="text-xs text-secondary truncate">{n.body}</p>
                    <p className="mt-0.5 text-[10px] text-muted-c">{n.customer ? `${n.customer} · ` : ""}{n.ref ? `${n.ref} · ` : ""}{timeAgo(n.created_at)}</p>
                  </div>
                  {!n.read && <span className="mt-1 h-1.5 w-1.5 rounded-full bg-gold" />}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
